(() => {
  const STORAGE_KEY = 'american-stride-delivery-zip';
  const config = document.querySelector('.custom-delivery-estimate-config');
  if (!config?.dataset.deliveryEndpoint) return;

  const normalizeZip = (value) => String(value || '').match(/^\s*(\d{5})(?:-\d{4})?\s*$/)?.[1] || '';

  const getZipValue = (field) => field.textContent.replace(/\s+/g, '');

  const setZipValue = (field, value) => {
    field.textContent = value || '';
  };

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Los_Angeles',
    }).format(new Date(Date.UTC(year, month - 1, day, 12)));
  };

  const createWidget = () => {
    const element = document.createElement('div');
    element.className = 'custom-delivery-estimate';
    element.setAttribute('role', 'region');
    element.setAttribute('aria-label', 'Estimated delivery');
    element.innerHTML = `
      <div class="custom-delivery-estimate__heading">
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="none"><path d="M3 6.5h11v10H3zM14 10h3.5l3.5 3.5v3H14zM6.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM17.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>
        <span>Estimated Delivery</span>
      </div>
      <p class="custom-delivery-estimate__dates" aria-live="polite">Calculating delivery dates…</p>
      <form class="custom-delivery-estimate__zip-form" novalidate>
        <span class="visually-hidden" id="CustomDeliveryZipLabel">Delivery ZIP code</span>
        <div id="CustomDeliveryZip" class="custom-delivery-estimate__zip" role="textbox" contenteditable="plaintext-only" inputmode="numeric" enterkeyhint="done" spellcheck="false" autocapitalize="off" aria-labelledby="CustomDeliveryZipLabel" aria-describedby="CustomDeliveryStatus" aria-required="true"></div>
        <span>if you order today</span>
        <button type="submit">Update</button>
      </form>
      <div class="custom-delivery-estimate__keypad" role="group" aria-label="ZIP code keypad" hidden>
        ${['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => `<button type="button" data-digit="${digit}">${digit}</button>`).join('')}
        <button type="button" data-action="backspace" aria-label="Delete last digit">⌫</button>
        <button type="button" data-digit="0">0</button>
        <button type="button" data-action="done">Done</button>
      </div>
      <p id="CustomDeliveryStatus" class="custom-delivery-estimate__status" aria-live="polite"></p>
      <p class="custom-delivery-estimate__express">Express option available at checkout.</p>
    `;
    return element;
  };

  const requestEstimate = async (widget, requestedZip = '') => {
    const dates = widget.querySelector('.custom-delivery-estimate__dates');
    const input = widget.querySelector('.custom-delivery-estimate__zip');
    const status = widget.querySelector('.custom-delivery-estimate__status');
    const button = widget.querySelector('button');
    const zip = normalizeZip(requestedZip);

    if (requestedZip && !zip) {
      input.setAttribute('aria-invalid', 'true');
      status.textContent = 'Please enter a valid 5-digit ZIP code.';
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');
    status.textContent = '';
    dates.textContent = 'Calculating delivery dates…';
    button.disabled = true;

    try {
      const url = new URL(config.dataset.deliveryEndpoint);
      if (zip) url.searchParams.set('zip', zip);
      const response = await fetch(url, { method: 'GET', mode: 'cors' });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || 'Estimate unavailable');

      setZipValue(input, payload.destinationZip);
      localStorage.setItem(STORAGE_KEY, payload.destinationZip);
      dates.innerHTML = `<strong>${formatDate(payload.earliestDate)}</strong><span> to </span><strong>${formatDate(payload.latestDate)}</strong>`;
      status.textContent = '';
    } catch {
      dates.textContent = 'Usually arrives within 3–10 business days';
      status.textContent = 'Enter your ZIP and try again for a current USPS estimate.';
    } finally {
      button.disabled = false;
    }
  };

  const initialize = (root = document) => {
    root.querySelectorAll('.product-form__quantity:not([data-custom-delivery-replaced])').forEach((quantity) => {
      quantity.dataset.customDeliveryReplaced = 'true';
      quantity.hidden = true;

      const widget = createWidget();
      quantity.insertAdjacentElement('beforebegin', widget);
      const input = widget.querySelector('.custom-delivery-estimate__zip');
      const keypad = widget.querySelector('.custom-delivery-estimate__keypad');
      const mobileKeypad = window.matchMedia('(max-width: 749px)').matches;
      const savedZip = normalizeZip(localStorage.getItem(STORAGE_KEY));
      const customerZip = normalizeZip(config.dataset.customerZip);
      const initialZip = savedZip || customerZip;
      if (initialZip) setZipValue(input, initialZip);

      if (mobileKeypad) {
        input.setAttribute('contenteditable', 'false');
        input.setAttribute('inputmode', 'none');
        input.setAttribute('tabindex', '0');
        input.setAttribute('aria-haspopup', 'true');
        input.setAttribute('aria-expanded', 'false');
        let replaceOnNextDigit = true;

        const openKeypad = () => {
          keypad.hidden = false;
          input.setAttribute('aria-expanded', 'true');
          replaceOnNextDigit = true;
        };

        input.addEventListener('click', openKeypad);
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          openKeypad();
        });

        keypad.addEventListener('click', (event) => {
          const key = event.target.closest('button');
          if (!key) return;
          const action = key.dataset.action;
          if (action === 'backspace') {
            replaceOnNextDigit = false;
            setZipValue(input, getZipValue(input).slice(0, -1));
            return;
          }
          if (action === 'done') {
            keypad.hidden = true;
            input.setAttribute('aria-expanded', 'false');
            widget.querySelector('form').requestSubmit();
            return;
          }
          if (key.dataset.digit) {
            if (replaceOnNextDigit) {
              setZipValue(input, key.dataset.digit);
              replaceOnNextDigit = false;
            } else if (getZipValue(input).length < 5) {
              setZipValue(input, `${getZipValue(input)}${key.dataset.digit}`);
            }
          }
        });
      }

      input.addEventListener('input', () => {
        const digits = getZipValue(input).replace(/\D/g, '').slice(0, 5);
        if (input.textContent !== digits) {
          setZipValue(input, digits);
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(input);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      });

      input.addEventListener('keydown', (event) => {
        if (mobileKeypad) return;
        if (event.key !== 'Enter') return;
        event.preventDefault();
        widget.querySelector('form').requestSubmit();
      });

      widget.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        requestEstimate(widget, getZipValue(input));
      });
      requestEstimate(widget, initialZip);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length)) initialize();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
